import {
  Suspense,
  useEffect,
  createContext,
  useState,
  useMemo,
  lazy,
} from 'react';

/// Components
import { connect, useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Index from './jsx';
// action
import { isAuthenticated as authenticatedFunc } from './store/selectors/AuthSelectors';
/// Style
import './scss/main.scss';
import { loginWithToken } from './store/actions/AuthActions';
import ErrorBoundary from './jsx/components/ErrorBoundary';

import { AdminCollectionsProvider } from './context/AdminCollections';
import { ProposerCollectionsProvider } from './context/ProposerCollections';
import { RefereeCollectionsProvider } from './context/RefereeReportCollections';
import { StaticCollectionsProvider } from './context/StaticCollections';
import { AdminActionCollectionsProvider } from './context/AdminActionCollections';
import { ChairReportStatusesProvider } from './context/ChairReportStatusesContext';
import { useLoggedInUserContextProvider } from './context/LoggedInUserContext';

const Login = lazy(() => import('./jsx/pages/Login'));

export const CommonCollections = createContext();
function withRouter(Component) {
  function ComponentWithRouterProp(props) {
    const location = useLocation();
    const navigate = useNavigate();
    const params = useParams();

    return <Component {...props} router={{ location, navigate, params }} />;
  }

  return ComponentWithRouterProp;
}

const App = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const urlParams = new URLSearchParams(window.location.search);
  const loginToken = urlParams.get('loginToken');

  const initialNominationIdFromToken = (() => {
    const token = urlParams.get('t');
    if (!token) return null;
    try {
      const raw = localStorage.getItem(`fns.view-nomination.${token}`);
      if (!raw) return null;
      localStorage.removeItem(`fns.view-nomination.${token}`);
      const url = new URL(window.location.href);
      url.searchParams.delete('t');
      window.history.replaceState({}, '', url.toString());
      const parsed = Number(raw);
      return Number.isFinite(parsed) ? parsed : null;
    } catch {
      return null;
    }
  })();

  // Methods
  const [nominationId, setNominationId] = useState(
    initialNominationIdFromToken,
  );
  const [
    nominationHistoryLastConsideredDate,
    setNominationHistoryLastConsideredDate,
  ] = useState(null);
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const [refereeReportId, setRefereeReportId] = useState(null);

  const { userDetails, setRefreshUserDetails, hasCheckedAuth } =
    useLoggedInUserContextProvider();

  useEffect(() => {
    if (loginToken) {
      dispatch(loginWithToken(loginToken, navigate, setRefreshUserDetails));
    }
  }, [dispatch, navigate, loginToken, setRefreshUserDetails]);

  return (
    <AdminCollectionsProvider>
      <ProposerCollectionsProvider>
        <RefereeCollectionsProvider>
          <StaticCollectionsProvider>
            <AdminActionCollectionsProvider>
              <ChairReportStatusesProvider>
                <CommonCollections.Provider
                  value={useMemo(
                    () => ({
                      nominationId,
                      setNominationId,
                      nominationHistoryLastConsideredDate,
                      setNominationHistoryLastConsideredDate,
                      refereeReportId,
                      setRefereeReportId,
                      isFormSubmitted,
                      setIsFormSubmitted,
                    }),
                    [
                      nominationId,
                      setNominationId,
                      nominationHistoryLastConsideredDate,
                      setNominationHistoryLastConsideredDate,
                      refereeReportId,
                      setRefereeReportId,
                      isFormSubmitted,
                      setIsFormSubmitted,
                    ],
                  )}
                >
                  <ErrorBoundary>
                    <div className='vh-100'>
                      <Suspense
                        fallback={
                          <div id='preloader'>
                            <div className='sk-three-bounce'>
                              <div className='sk-child sk-bounce1' />
                              <div className='sk-child sk-bounce2' />
                              <div className='sk-child sk-bounce3' />
                            </div>
                          </div>
                        }
                      >
                        {!hasCheckedAuth ? (
                          <div id='preloader'>
                            <div className='sk-three-bounce'>
                              <div className='sk-child sk-bounce1' />
                              <div className='sk-child sk-bounce2' />
                              <div className='sk-child sk-bounce3' />
                            </div>
                          </div>
                        ) : userDetails ? (
                          <Index />
                        ) : (
                          <Login />
                        )}
                      </Suspense>
                    </div>
                  </ErrorBoundary>
                </CommonCollections.Provider>
              </ChairReportStatusesProvider>
            </AdminActionCollectionsProvider>
          </StaticCollectionsProvider>
        </RefereeCollectionsProvider>
      </ProposerCollectionsProvider>
    </AdminCollectionsProvider>
  );
};

const mapStateToProps = (state) => ({
  isAuthenticated: authenticatedFunc(state),
});

export default withRouter(connect(mapStateToProps)(App));
